import { useEffect, useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";
import { User } from '@supabase/supabase-js';

interface TabSyncMessage {
  type: 'GLOBAL_LOGOUT' | 'SESSION_REFRESH' | 'USER_ACTIVITY';
  timestamp: number;
  user_id?: string;
  session_id?: string;
  metadata?: Record<string, any>;
}

const BROADCAST_CHANNEL_NAME = 'auth_events';
const STORAGE_EVENT_KEY = 'auth_sync_event';

export function useMultiTabSync(user: User | null) {
  // Remove the useAuth call since we now receive user as parameter
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isInitiatedByThisTab = useRef(false);

  // Broadcast a message to all tabs
  const broadcast = useCallback((message: TabSyncMessage) => {
    isInitiatedByThisTab.current = true;
    
    // Primary: BroadcastChannel API (modern browsers)
    if (channelRef.current) {
      try {
        channelRef.current.postMessage(message);
      } catch (error) {
        // Fallback to localStorage
      }
    }

    // Fallback: localStorage events (all browsers)
    try {
      // Add UUID to make each event unique
      const id = crypto.randomUUID();
      const eventData = {
        ...message,
        timestamp: Date.now(),
        sender_tab: window.name || 'unknown',
        id
      };
      
      // Use unique key to prevent race conditions
      const key = `${STORAGE_EVENT_KEY}_${eventData.id}`;
      localStorage.setItem(key, JSON.stringify(eventData));
      
      // Only remove if still the same value (prevents race condition)
      setTimeout(() => {
        const current = localStorage.getItem(key);
        if (current === JSON.stringify(eventData)) {
          localStorage.removeItem(key);
        }
      }, 100);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'storage_error', operation: 'multi_tab_sync' },
        extra: { 
          quotaExceeded: error instanceof Error && error.name === 'QuotaExceededError',
          message: 'localStorage broadcast failed'
        }
      });

    }

    // Reset flag after broadcast
    setTimeout(() => {
      isInitiatedByThisTab.current = false;
    }, 1000);
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback(async (message: TabSyncMessage) => {
    // Ignore messages from this tab
    if (isInitiatedByThisTab.current) return;
    
    // Check if component is still mounted before processing
    if (!user) return;

    switch (message.type) {
      case 'GLOBAL_LOGOUT':
        if (user && message.user_id === user.id) {
          // Clear sensitive data immediately
          try {
            const sensitiveKeys = [
              'assessment_progress', 'bookmarked_resources', 'taskSavedFilters',
              'criticalErrors', 'bundle_violations', 'user_preferences',
              'draft_data', 'temp_uploads'
            ];
            
            // Clear sensitive keys
            sensitiveKeys.forEach(key => {
              localStorage.removeItem(key);
              sessionStorage.removeItem(key);
            });
            
            // Clear cache_ prefixed keys
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('cache_')) {
                localStorage.removeItem(key);
              }
            });
            
          } catch (error) {
            Sentry.captureException(error, {
              tags: { context: 'data_cleanup', component: 'useMultiTabSync' },
              extra: { message: 'Failed to clear sensitive data' }
            });
          }

          // Force redirect to login without calling signOut (already done in other tab)
          window.location.href = '/auth/login';
        }
        break;

      case 'SESSION_REFRESH':
        if (user && message.user_id === user.id) {
          // Force re-authentication check
          window.location.reload();
        }
        break;

      case 'USER_ACTIVITY':
        // Handle cross-tab activity updates if needed
        break;

      default:
        break;
    }
  }, [user]);

  // Initialize multi-tab communication
  useEffect(() => {
    if (!user) return;

    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channelRef.current.addEventListener('message', (event) => {
          handleMessage(event.data);
        });
      } catch (error) {
        // BroadcastChannel not supported
      }
    }

    // Setup localStorage listener for fallback
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_EVENT_KEY && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as TabSyncMessage;
          handleMessage(message);
        } catch (error) {
          // Invalid storage event
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      // Cleanup
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user, handleMessage]);

  // Broadcast global logout
  const broadcastGlobalLogout = useCallback((session_id?: string, metadata?: Record<string, any>) => {
    if (!user) return;

    broadcast({
      type: 'GLOBAL_LOGOUT',
      timestamp: Date.now(),
      user_id: user.id,
      session_id,
      metadata
    });
  }, [user, broadcast]);

  // Broadcast session refresh
  const broadcastSessionRefresh = useCallback(() => {
    if (!user) return;

    broadcast({
      type: 'SESSION_REFRESH',
      timestamp: Date.now(),
      user_id: user.id
    });
  }, [user, broadcast]);

  // Broadcast user activity
  const broadcastActivity = useCallback(() => {
    if (!user) return;

    broadcast({
      type: 'USER_ACTIVITY',
      timestamp: Date.now(),
      user_id: user.id
    });
  }, [user, broadcast]);

  return {
    broadcastGlobalLogout,
    broadcastSessionRefresh,
    broadcastActivity,
    isSupported: typeof BroadcastChannel !== 'undefined' || typeof localStorage !== 'undefined'
  };
}